import {isWithinFilePickerGracePeriod} from "@/src/modules/core/lib/file-picker-session";

const VERSION_URL = "/version.json";
const DEBOUNCE_MS = 500;
/** Prevent stacked focus/SW/burst triggers from hammering version.json. */
const MIN_FETCH_INTERVAL_MS = 2_000;

type VersionPayload = {
  version: string;
  buildId: string;
};

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let fetchInFlight: Promise<string | null> | null = null;
let lastFetchAt = 0;
let lastRemoteBuildId: string | null = null;
let immediateCheckListener: (() => void) | null = null;
let pendingImmediateVersionCheck = false;
let deployBurstOnNewBuild: (() => void) | null = null;
let deployBurstTimer: ReturnType<typeof setInterval> | null = null;

export function subscribeImmediateVersionChecks(
  listener: () => void,
  onNewBuild: () => void,
) {
  immediateCheckListener = listener;
  deployBurstOnNewBuild = onNewBuild;
  if (pendingImmediateVersionCheck) {
    pendingImmediateVersionCheck = false;
    listener();
  }
  return () => {
    if (immediateCheckListener === listener) {
      immediateCheckListener = null;
      deployBurstOnNewBuild = null;
    }
  };
}

/** Fired when the SW detects a new deploy — bypasses the poll interval. */
export function requestImmediateVersionCheck() {
  if (immediateCheckListener) {
    immediateCheckListener();
  } else {
    pendingImmediateVersionCheck = true;
  }
  if (deployBurstOnNewBuild) {
    startDeployDetectionBurst(deployBurstOnNewBuild);
  }
}

export function stopDeployDetectionBurst() {
  if (deployBurstTimer) {
    clearInterval(deployBurstTimer);
    deployBurstTimer = null;
  }
}

/** After SW sees a new build, poll version.json until upload finishes or timeout. */
export function startDeployDetectionBurst(onNewBuild: () => void) {
  if (deployBurstTimer) {
    return;
  }

  let attempts = 0;
  const maxAttempts = 20;

  const tick = () => {
    attempts += 1;
    void runVersionCheck(onNewBuild);
    if (attempts >= maxAttempts) {
      stopDeployDetectionBurst();
    }
  };

  void runVersionCheck(onNewBuild);
  deployBurstTimer = setInterval(tick, 2_000);
}

export async function fetchRemoteBuildId(): Promise<string | null> {
  if (!navigator.onLine) {
    return null;
  }

  if (fetchInFlight) {
    return fetchInFlight;
  }

  const now = Date.now();
  if (lastFetchAt > 0 && now - lastFetchAt < MIN_FETCH_INTERVAL_MS) {
    return lastRemoteBuildId;
  }

  fetchInFlight = (async () => {
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });
      if (!response.ok) {
        return null;
      }
      const data = (await response.json()) as VersionPayload;
      lastRemoteBuildId = data.buildId ?? null;
      return lastRemoteBuildId;
    } catch {
      return null;
    } finally {
      lastFetchAt = Date.now();
    }
  })();

  try {
    return await fetchInFlight;
  } finally {
    fetchInFlight = null;
  }
}

export function requestServiceWorkerUpdate() {
  if (!("serviceWorker" in navigator)) {
    return;
  }
  void navigator.serviceWorker.ready.then((registration) => registration.update());
}

export function scheduleVersionCheck(
  onNewBuild: () => void,
  options?: {immediate?: boolean},
) {
  if (options?.immediate) {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    void runVersionCheck(onNewBuild);
    return;
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void runVersionCheck(onNewBuild);
  }, DEBOUNCE_MS);
}

async function runVersionCheck(onNewBuild: () => void) {
  if (isWithinFilePickerGracePeriod()) {
    return;
  }

  const remoteBuildId = await fetchRemoteBuildId();
  if (remoteBuildId && remoteBuildId !== APP_BUILD_ID) {
    stopDeployDetectionBurst();
    onNewBuild();
  }
}
